import { useCallback, useEffect, useMemo, useState } from 'react'
import {
  Button,
  Card,
  Empty,
  Form,
  Input,
  Modal,
  Segmented,
  Space,
  Spin,
  Switch,
  Table,
  Tabs,
  Tag,
  Tooltip,
  Tree,
  Typography,
  message,
} from 'antd'
import {
  CopyOutlined,
  DeleteOutlined,
  EditOutlined,
  EyeOutlined,
  FolderOutlined,
  PlusOutlined,
  SyncOutlined,
} from '@ant-design/icons'
import type { DataNode } from 'antd/es/tree'
import { Link } from 'react-router-dom'
import { adminApi, formatApiError, PAGE_SIZE, type PageResult } from '../../api'
import { PageHeader, tablePagination, formatDate } from '../../components/ui'

type RuleVirtualClassification = {
  id: number
  name: string
  scope: string
}

type RuleRow = {
  id: number
  name: string
  description: string
  is_default: boolean
  is_active: boolean
  auto_assign_folder: boolean
  sort_order: number
  category_count: number
  dimension_scopes: string[]
  virtual_classifications: RuleVirtualClassification[]
  virtual_classification_count: number
}

type VirtualClassificationRow = {
  id: number
  rule_id: number
  rule_name: string
  scope: string
  name: string
  description: string
  show_in_tree: boolean
  last_synced_at: string | null
}

type PreviewTreeNode = {
  key: string
  title: string
  path?: string
  children?: PreviewTreeNode[]
}

type RuleForm = {
  name: string
  description: string
  is_active: boolean
  is_default: boolean
  auto_assign_folder: boolean
  sort_order: number
}

const scopeLabel: Record<string, string> = {
  enterprise: '企业知识库',
  personal: '个人知识库',
}

const COPY_NAME_SUFFIX = '-副本'

const PREVIEW_MODAL_BODY_HEIGHT = 480
const PREVIEW_TREE_PANEL_HEIGHT = 400

function countTreeStats(nodes: PreviewTreeNode[]): { total: number; maxDepth: number } {
  let total = 0
  let maxDepth = 0
  const walk = (list: PreviewTreeNode[], depth: number) => {
    for (const node of list) {
      total += 1
      maxDepth = Math.max(maxDepth, depth)
      if (node.children?.length) walk(node.children, depth + 1)
    }
  }
  walk(nodes, 1)
  return { total, maxDepth }
}

function toAntTree(nodes: PreviewTreeNode[]): DataNode[] {
  return nodes.map((n) => ({
    key: n.key,
    title: n.title,
    icon: <FolderOutlined />,
    children: n.children?.length ? toAntTree(n.children) : undefined,
  }))
}

export default function ClassificationManage() {
  const [tab, setTab] = useState('rules')
  const [items, setItems] = useState<RuleRow[]>([])
  const [total, setTotal] = useState(0)
  const [page, setPage] = useState(1)
  const [loading, setLoading] = useState(false)
  const [dimensions, setDimensions] = useState<VirtualClassificationRow[]>([])
  const [dimTotal, setDimTotal] = useState(0)
  const [dimPage, setDimPage] = useState(1)
  const [dimLoading, setDimLoading] = useState(false)
  const [createModalOpen, setCreateModalOpen] = useState(false)
  const [editModalOpen, setEditModalOpen] = useState(false)
  const [editingRule, setEditingRule] = useState<RuleRow | null>(null)
  const [dimModalOpen, setDimModalOpen] = useState(false)
  const [editDimModalOpen, setEditDimModalOpen] = useState(false)
  const [editingDimension, setEditingDimension] = useState<VirtualClassificationRow | null>(null)
  const [editDimName, setEditDimName] = useState('')
  const [dimSaving, setDimSaving] = useState(false)
  const [dimTargetRule, setDimTargetRule] = useState<RuleRow | null>(null)
  const [dimScope, setDimScope] = useState<'enterprise' | 'personal'>('enterprise')
  const [dimName, setDimName] = useState('')
  const [dimCreating, setDimCreating] = useState(false)
  const [ruleDimNames, setRuleDimNames] = useState<string[]>([])
  const [editSiblingDimNames, setEditSiblingDimNames] = useState<string[]>([])
  const [copyingId, setCopyingId] = useState<number | null>(null)
  const [deletingId, setDeletingId] = useState<number | null>(null)
  const [dimDeletingId, setDimDeletingId] = useState<number | null>(null)
  const [dimTogglingTreeId, setDimTogglingTreeId] = useState<number | null>(null)
  const [syncingId, setSyncingId] = useState<number | null>(null)
  const [previewOpen, setPreviewOpen] = useState(false)
  const [previewLoading, setPreviewLoading] = useState(false)
  const [previewTitle, setPreviewTitle] = useState('')
  const [previewSource, setPreviewSource] = useState<'rule' | 'synced'>('rule')
  const [previewSyncedAt, setPreviewSyncedAt] = useState<string | null>(null)
  const [previewRawNodes, setPreviewRawNodes] = useState<PreviewTreeNode[]>([])
  const [previewExpandedKeys, setPreviewExpandedKeys] = useState<string[]>([])
  const [saving, setSaving] = useState(false)
  const [createForm] = Form.useForm<RuleForm>()
  const [editForm] = Form.useForm<RuleForm>()

  const previewStats = useMemo(() => countTreeStats(previewRawNodes), [previewRawNodes])
  const previewTreeData = useMemo(() => toAntTree(previewRawNodes), [previewRawNodes])
  const createDimNameDuplicate = useMemo(() => {
    const n = dimName.trim()
    return n.length > 0 && ruleDimNames.includes(n)
  }, [dimName, ruleDimNames])
  const editDimNameDuplicate = useMemo(() => {
    const n = editDimName.trim()
    return n.length > 0 && editSiblingDimNames.includes(n)
  }, [editDimName, editSiblingDimNames])

  const loadRules = useCallback(() => {
    setLoading(true)
    return adminApi
      .get<PageResult<RuleRow>>('/admin/classification/rules', {
        params: { page, page_size: PAGE_SIZE },
      })
      .then((r) => {
        setItems(r.data.items || [])
        setTotal(r.data.total || 0)
      })
      .catch((e) => message.error(formatApiError(e, '加载规则失败')))
      .finally(() => setLoading(false))
  }, [page])

  const loadDimensions = useCallback(() => {
    setDimLoading(true)
    return adminApi
      .get<PageResult<VirtualClassificationRow>>('/admin/classification/virtual-dimensions', {
        params: { page: dimPage, page_size: PAGE_SIZE },
      })
      .then((r) => {
        setDimensions(r.data.items || [])
        setDimTotal(r.data.total || 0)
      })
      .catch((e) => message.error(formatApiError(e, '加载虚拟分类失败')))
      .finally(() => setDimLoading(false))
  }, [dimPage])

  useEffect(() => {
    if (tab === 'rules') void loadRules()
  }, [tab, loadRules])

  useEffect(() => {
    if (tab === 'dimensions') void loadDimensions()
  }, [tab, loadDimensions])

  useEffect(() => {
    if (!dimModalOpen || !dimTargetRule) {
      setRuleDimNames([])
      return
    }
    adminApi
      .get<{ items: { name: string; scope: string }[] }>(
        `/admin/classification/rules/${dimTargetRule.id}/dimensions`,
      )
      .then((r) => {
        const names = (r.data.items || [])
          .filter((d) => d.scope === dimScope)
          .map((d) => d.name)
        setRuleDimNames(names)
      })
      .catch(() => setRuleDimNames([]))
  }, [dimModalOpen, dimTargetRule, dimScope])

  useEffect(() => {
    if (!editDimModalOpen || !editingDimension) {
      setEditSiblingDimNames([])
      return
    }
    adminApi
      .get<{ items: { id: number; name: string; scope: string }[] }>(
        `/admin/classification/rules/${editingDimension.rule_id}/dimensions`,
      )
      .then((r) => {
        const names = (r.data.items || [])
          .filter((d) => d.scope === editingDimension.scope && d.id !== editingDimension.id)
          .map((d) => d.name)
        setEditSiblingDimNames(names)
      })
      .catch(() => setEditSiblingDimNames([]))
  }, [editDimModalOpen, editingDimension])

  const openCreate = () => {
    createForm.setFieldsValue({
      name: '',
      description: '',
      is_active: true,
      is_default: false,
      auto_assign_folder: true,
      sort_order: 0,
    })
    setCreateModalOpen(true)
  }

  const openEdit = (rule: RuleRow) => {
    setEditingRule(rule)
    editForm.setFieldsValue({
      name: rule.name,
      description: rule.description || '',
      is_active: rule.is_active,
      is_default: rule.is_default,
      auto_assign_folder: rule.auto_assign_folder,
      sort_order: rule.sort_order ?? 0,
    })
    setEditModalOpen(true)
  }

  const openCreateDimension = (rule: RuleRow) => {
    setDimTargetRule(rule)
    setDimScope('enterprise')
    setDimName(rule.name)
    setDimModalOpen(true)
  }

  const [editDimRuleName, setEditDimRuleName] = useState('')

  const openEditDimension = (row: VirtualClassificationRow) => {
    setEditingDimension(row)
    setEditDimRuleName(row.rule_name || '')
    setEditDimName(row.name)
    setEditDimModalOpen(true)
    if (row.rule_id) {
      adminApi
        .get<{ name: string }>(`/admin/classification/rules/${row.rule_id}`)
        .then((r) => setEditDimRuleName(r.data.name || ''))
        .catch(() => setEditDimRuleName(row.rule_name || ''))
    }
  }

  const saveEditDimension = () => {
    if (!editingDimension) return
    const name = editDimName.trim()
    if (!name) {
      message.warning('请填写虚拟分类名称')
      return
    }
    if (editDimNameDuplicate) {
      message.warning('该规则在当前知识库下已存在同名虚拟分类，请更换名称')
      return
    }
    setDimSaving(true)
    adminApi
      .put(`/admin/classification/dimensions/${editingDimension.id}`, { name })
      .then(() => {
        message.success('名称已保存')
        setEditDimModalOpen(false)
        setEditingDimension(null)
        void loadDimensions()
        void loadRules()
      })
      .catch((e) => message.error(formatApiError(e, '保存失败')))
      .finally(() => setDimSaving(false))
  }

  const previewDimensionTree = (row: VirtualClassificationRow) => {
    setPreviewTitle(row.name)
    setPreviewOpen(true)
    setPreviewLoading(true)
    setPreviewRawNodes([])
    setPreviewExpandedKeys([])
    setPreviewSyncedAt(null)
    adminApi
      .get<{ source: 'rule' | 'synced'; tree: PreviewTreeNode[]; last_synced_at?: string | null }>(
        `/admin/classification/dimensions/${row.id}/tree-preview`,
      )
      .then((r) => {
        setPreviewSource(r.data.source === 'synced' ? 'synced' : 'rule')
        setPreviewSyncedAt(r.data.last_synced_at ?? row.last_synced_at)
        setPreviewRawNodes(r.data.tree || [])
      })
      .catch((e) => message.error(formatApiError(e, '加载目录预览失败')))
      .finally(() => setPreviewLoading(false))
  }

  const saveCreateRule = () => {
    void createForm.validateFields().then((v) => {
      setSaving(true)
      adminApi
        .post('/admin/classification/rules', {
          name: v.name.trim(),
          description: (v.description || '').trim(),
          is_active: v.is_active,
          is_default: v.is_default,
          auto_assign_folder: v.auto_assign_folder,
          sort_order: v.sort_order ?? 0,
        })
        .then(() => {
          message.success('已创建分类规则，点击规则名称可管理文档类型')
          setCreateModalOpen(false)
          void loadRules()
        })
        .catch((e) => message.error(formatApiError(e, '创建失败')))
        .finally(() => setSaving(false))
    })
  }

  const saveEditRule = () => {
    if (!editingRule) return
    void editForm.validateFields().then((v) => {
      setSaving(true)
      adminApi
        .put(`/admin/classification/rules/${editingRule.id}`, {
          name: v.name.trim(),
          description: (v.description || '').trim(),
          is_active: v.is_active,
          is_default: v.is_default,
          auto_assign_folder: v.auto_assign_folder,
          sort_order: v.sort_order ?? 0,
        })
        .then(() => {
          message.success('已保存')
          setEditModalOpen(false)
          setEditingRule(null)
          void loadRules()
          void loadDimensions()
        })
        .catch((e) => message.error(formatApiError(e, '保存失败')))
        .finally(() => setSaving(false))
    })
  }

  const copyRule = (rule: RuleRow) => {
    Modal.confirm({
      title: '复制分类规则',
      content: (
        <span>
          是否复制规则「{rule.name}」？将创建一条新规则，名称为「{rule.name}-副本」
          {rule.name.length + COPY_NAME_SUFFIX.length > 64 ? '（名称过长时将截断）' : ''}
          ；若已存在同名规则，将自动加序号避免重名。
        </span>
      ),
      okText: '复制',
      cancelText: '取消',
      onOk: () => {
        setCopyingId(rule.id)
        return adminApi
          .post(`/admin/classification/rules/${rule.id}/copy`)
          .then((r) => {
            message.success(`已复制为「${r.data.name}」`)
            void loadRules()
          })
          .catch((e) => {
            message.error(formatApiError(e, '复制失败'))
            return Promise.reject(e)
          })
          .finally(() => setCopyingId(null))
      },
    })
  }

  const deleteRule = (rule: RuleRow) => {
    Modal.confirm({
      title: `删除分类规则「${rule.name}」？`,
      content:
        total <= 1
          ? '至少保留一条分类规则，无法删除。'
          : '将同时删除该规则下的文档类型与虚拟分类目录；若已有文档使用此规则分类，将无法删除。',
      okText: '删除',
      okButtonProps: { danger: true, disabled: total <= 1 },
      cancelText: '取消',
      onOk: () => {
        if (total <= 1) return Promise.reject(new Error('至少保留一条分类规则'))
        setDeletingId(rule.id)
        return adminApi
          .delete(`/admin/classification/rules/${rule.id}`)
          .then(() => {
            message.success('已删除')
            void loadRules()
          })
          .catch((e) => {
            message.error(formatApiError(e, '删除失败'))
            return Promise.reject(e)
          })
          .finally(() => setDeletingId(null))
      },
    })
  }

  const confirmCreateDimension = () => {
    if (!dimTargetRule) return
    const name = dimName.trim()
    if (!name) {
      message.warning('请填写虚拟分类名称')
      return
    }
    if (createDimNameDuplicate) {
      message.warning('该规则在当前知识库下已存在同名虚拟分类，请更换名称')
      return
    }
    setDimCreating(true)
    adminApi
      .post(`/admin/classification/rules/${dimTargetRule.id}/dimensions`, {
        scope: dimScope,
        name,
      })
      .then((r) => {
        const synced = r.data.sync_stats?.synced_categories
        message.success(
          synced != null
            ? `已生成虚拟分类，并同步 ${synced} 个目录节点`
            : '已生成虚拟分类（规则下暂无文档类型，未生成目录）',
        )
        setDimModalOpen(false)
        void loadRules()
        if (tab === 'dimensions') void loadDimensions()
        else setTab('dimensions')
      })
      .catch((e) => message.error(formatApiError(e, '创建失败')))
      .finally(() => setDimCreating(false))
  }

  const toggleDimensionShowInTree = (row: VirtualClassificationRow, checked: boolean) => {
    setDimTogglingTreeId(row.id)
    adminApi
      .put(`/admin/classification/dimensions/${row.id}`, { show_in_tree: checked })
      .then(() => {
        message.success(checked ? '已设为前台展示' : '已设为前台不展示')
        void loadDimensions()
      })
      .catch((e) => message.error(formatApiError(e, '更新失败')))
      .finally(() => setDimTogglingTreeId(null))
  }

  const deleteDimension = (row: VirtualClassificationRow) => {
    Modal.confirm({
      title: '删除虚拟分类',
      content: `确定删除「${row.name}」？其目录树与文档落点关联将被移除，此操作不可恢复。`,
      okText: '删除',
      okType: 'danger',
      cancelText: '取消',
      onOk: () => {
        setDimDeletingId(row.id)
        return adminApi
          .delete(`/admin/classification/dimensions/${row.id}`)
          .then(() => {
            message.success('已删除')
            void loadDimensions()
            void loadRules()
          })
          .catch((e) => {
            message.error(formatApiError(e, '删除失败'))
            return Promise.reject(e)
          })
          .finally(() => setDimDeletingId(null))
      },
    })
  }

  const syncDimension = (dimensionId: number) => {
    setSyncingId(dimensionId)
    adminApi
      .post(`/admin/classification/dimensions/${dimensionId}/sync`)
      .then((r) => {
        message.success(`已同步 ${r.data.synced_categories ?? 0} 个目录节点`)
        void loadDimensions()
      })
      .catch((e) => message.error(formatApiError(e, '同步失败')))
      .finally(() => setSyncingId(null))
  }

  const ruleFormFields = () => (
    <>
      <Form.Item name="name" label="规则名称" rules={[{ required: true, message: '请输入规则名称' }]}>
        <Input maxLength={64} placeholder="如：制度文档分类" />
      </Form.Item>
      <Form.Item name="description" label="说明">
        <Input.TextArea rows={3} maxLength={512} placeholder="可选" />
      </Form.Item>
      <Form.Item
        name="is_default"
        label="设为当前自动分类规则"
        valuePropName="checked"
        extra="全库仅一条规则用于新建文档、上传与采集时的自动分类"
      >
        <Switch />
      </Form.Item>
      <Form.Item
        name="auto_assign_folder"
        label="分类后自动入目录"
        valuePropName="checked"
        extra="开启后，文档完成分类会自动归入该规则下各虚拟分类中匹配的目录"
      >
        <Switch />
      </Form.Item>
      <Form.Item name="is_active" label="启用" valuePropName="checked">
        <Switch />
      </Form.Item>
    </>
  )

  return (
    <div>
      <PageHeader
        title="智能分类"
        description="可维护多条分类规则，但仅一条为「当前自动分类」，用于新建、上传与采集；点击规则名称管理文档类型层级。"
      />

      <Card>
        <Tabs
          activeKey={tab}
          onChange={setTab}
          items={[
            {
              key: 'rules',
              label: '分类规则',
              children: (
                <>
                  <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 16 }}>
                    <Button type="primary" icon={<PlusOutlined />} onClick={openCreate}>
                      新建规则
                    </Button>
                  </div>
                  <Table<RuleRow>
                    rowKey="id"
                    loading={loading}
                    dataSource={items}
                    pagination={tablePagination(page, total, setPage)}
                    columns={[
                      {
                        title: '规则名称',
                        dataIndex: 'name',
                        render: (name: string, row) => (
                          <Link to={`/manage/classification/${row.id}`}>{name}</Link>
                        ),
                      },
                      {
                        title: '说明',
                        dataIndex: 'description',
                        ellipsis: true,
                        render: (t: string) => t || '—',
                      },
                      {
                        title: '类型数',
                        dataIndex: 'category_count',
                        width: 88,
                      },
                      {
                        title: '虚拟分类',
                        dataIndex: 'virtual_classifications',
                        ellipsis: true,
                        render: (items: RuleVirtualClassification[] | undefined) => {
                          if (!items?.length) {
                            return (
                              <Typography.Text type="secondary">无</Typography.Text>
                            )
                          }
                          return items.map((d) => d.name).join('、')
                        },
                      },
                      {
                        title: (
                          <Tooltip title="分类完成后，是否自动将文档放入该规则虚拟分类下匹配的目录">
                            分类后入目录
                          </Tooltip>
                        ),
                        dataIndex: 'auto_assign_folder',
                        width: 120,
                        render: (v: boolean) => (v ? '是' : '否'),
                      },
                      {
                        title: '状态',
                        width: 140,
                        render: (_, row) => (
                          <Space size={4} wrap>
                            {row.is_default ? <Tag color="blue">当前自动分类</Tag> : null}
                            <Tag color={row.is_active ? 'green' : 'default'}>
                              {row.is_active ? '启用' : '停用'}
                            </Tag>
                          </Space>
                        ),
                      },
                      {
                        title: '操作',
                        width: 360,
                        render: (_, row) => (
                          <Space wrap>
                            <Button size="small" onClick={() => openEdit(row)}>
                              编辑
                            </Button>
                            <Button
                              size="small"
                              icon={<CopyOutlined />}
                              loading={copyingId === row.id}
                              onClick={() => copyRule(row)}
                            >
                              复制
                            </Button>
                            <Button size="small" onClick={() => openCreateDimension(row)}>
                              生成虚拟分类
                            </Button>
                            <Tooltip title={total <= 1 ? '至少保留一条分类规则' : undefined}>
                              <Button
                                size="small"
                                danger
                                icon={<DeleteOutlined />}
                                loading={deletingId === row.id}
                                disabled={total <= 1}
                                onClick={() => deleteRule(row)}
                              >
                                删除
                              </Button>
                            </Tooltip>
                          </Space>
                        ),
                      },
                    ]}
                  />
                </>
              ),
            },
            {
              key: 'dimensions',
              label: '虚拟分类',
              children: (
                <>
                  <Typography.Paragraph type="secondary" style={{ marginTop: 0 }}>
                    虚拟分类绑定分类规则，用于生成前台知识库目录树。同一规则可创建多个虚拟分类；可预览规则生成的目录层级，同步后写入实际文件夹。
                  </Typography.Paragraph>
                  <Table<VirtualClassificationRow>
                    rowKey="id"
                    loading={dimLoading}
                    dataSource={dimensions}
                    pagination={tablePagination(dimPage, dimTotal, setDimPage)}
                    columns={[
                      { title: '分类名称', dataIndex: 'name', ellipsis: true },
                      {
                        title: '绑定规则',
                        dataIndex: 'rule_name',
                        ellipsis: true,
                        render: (name: string, row) => name || `#${row.rule_id}`,
                      },
                      {
                        title: '知识库',
                        dataIndex: 'scope',
                        width: 120,
                        render: (scope: string) => scopeLabel[scope] || scope,
                      },
                      {
                        title: '前台展示',
                        dataIndex: 'show_in_tree',
                        width: 130,
                        render: (show: boolean, row) => (
                          <Space size={6}>
                            <Switch
                              size="small"
                              checked={show !== false}
                              loading={dimTogglingTreeId === row.id}
                              onChange={(checked) => toggleDimensionShowInTree(row, checked)}
                            />
                            <Tag color={show !== false ? 'green' : 'default'}>
                              {show !== false ? '展示' : '不展示'}
                            </Tag>
                          </Space>
                        ),
                      },
                      {
                        title: '最近同步',
                        dataIndex: 'last_synced_at',
                        width: 180,
                        render: (t: string | null) => formatDate(t) || '—',
                      },
                      {
                        title: '操作',
                        width: 340,
                        render: (_, row) => (
                          <Space wrap>
                            <Button size="small" icon={<EditOutlined />} onClick={() => openEditDimension(row)}>
                              改名
                            </Button>
                            <Button
                              size="small"
                              icon={<EyeOutlined />}
                              onClick={() => previewDimensionTree(row)}
                            >
                              预览目录
                            </Button>
                            <Button
                              size="small"
                              icon={<SyncOutlined />}
                              loading={syncingId === row.id}
                              onClick={() => syncDimension(row.id)}
                            >
                              同步目录
                            </Button>
                            <Button
                              size="small"
                              danger
                              icon={<DeleteOutlined />}
                              loading={dimDeletingId === row.id}
                              onClick={() => deleteDimension(row)}
                            >
                              删除
                            </Button>
                          </Space>
                        ),
                      },
                    ]}
                  />
                </>
              ),
            },
          ]}
        />
      </Card>

      <Modal
        title="新建分类规则"
        open={createModalOpen}
        onCancel={() => setCreateModalOpen(false)}
        onOk={saveCreateRule}
        confirmLoading={saving}
        okText="创建"
        cancelText="取消"
        destroyOnHidden
      >
        <Form
          form={createForm}
          layout="vertical"
          initialValues={{ is_active: true, is_default: false, auto_assign_folder: true, sort_order: 0 }}
        >
          {ruleFormFields()}
        </Form>
      </Modal>

      <Modal
        title="编辑分类规则"
        open={editModalOpen}
        onCancel={() => {
          setEditModalOpen(false)
          setEditingRule(null)
        }}
        onOk={saveEditRule}
        confirmLoading={saving}
        okText="保存"
        cancelText="取消"
        destroyOnHidden
      >
        <Form form={editForm} layout="vertical">
          {ruleFormFields()}
        </Form>
      </Modal>

      <Modal
        title="生成虚拟分类"
        open={dimModalOpen}
        onCancel={() => setDimModalOpen(false)}
        onOk={confirmCreateDimension}
        confirmLoading={dimCreating}
        okText="生成"
        cancelText="取消"
        destroyOnHidden
      >
        <Space direction="vertical" style={{ width: '100%' }} size="middle">
          <Typography.Text>
            绑定规则：<strong>{dimTargetRule?.name || '—'}</strong>
          </Typography.Text>
          <div>
            <Typography.Text type="secondary" style={{ display: 'block', marginBottom: 8 }}>
              知识库范围（同一规则可创建多个虚拟分类）
            </Typography.Text>
            <Segmented
              value={dimScope}
              onChange={(v) => setDimScope(v as 'enterprise' | 'personal')}
              options={[
                { label: '企业知识库', value: 'enterprise' },
                { label: '个人知识库', value: 'personal' },
              ]}
            />
          </div>
          <div>
            <Typography.Text type="secondary" style={{ display: 'block', marginBottom: 8 }}>
              分类名称（默认带入规则名，可修改）
            </Typography.Text>
            <Input
              maxLength={64}
              value={dimName}
              onChange={(e) => setDimName(e.target.value)}
              placeholder="虚拟分类名称"
              status={createDimNameDuplicate ? 'error' : undefined}
            />
            {createDimNameDuplicate ? (
              <Typography.Text type="danger" style={{ fontSize: 12 }}>
                该规则在当前知识库下已存在同名虚拟分类，请更换名称
              </Typography.Text>
            ) : ruleDimNames.length ? (
              <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                已用名称：{ruleDimNames.join('、')}
              </Typography.Text>
            ) : null}
          </div>
        </Space>
      </Modal>

      <Modal
        title="修改虚拟分类名称"
        open={editDimModalOpen}
        onCancel={() => {
          setEditDimModalOpen(false)
          setEditingDimension(null)
        }}
        onOk={saveEditDimension}
        confirmLoading={dimSaving}
        okText="保存"
        cancelText="取消"
        destroyOnHidden
      >
        <Space direction="vertical" style={{ width: '100%' }} size="middle">
          <Typography.Text>
            绑定规则：<strong>{editDimRuleName || editingDimension?.rule_name || '—'}</strong>
          </Typography.Text>
          <Typography.Text type="secondary">
            知识库：{scopeLabel[editingDimension?.scope || ''] || editingDimension?.scope || '—'}
          </Typography.Text>
          <div>
            <Typography.Text type="secondary" style={{ display: 'block', marginBottom: 8 }}>
              分类名称
            </Typography.Text>
            <Input
              maxLength={64}
              value={editDimName}
              onChange={(e) => setEditDimName(e.target.value)}
              placeholder="虚拟分类名称"
              status={editDimNameDuplicate ? 'error' : undefined}
            />
            {editDimNameDuplicate ? (
              <Typography.Text type="danger" style={{ fontSize: 12 }}>
                该规则在当前知识库下已存在同名虚拟分类，请更换名称
              </Typography.Text>
            ) : null}
          </div>
        </Space>
      </Modal>

      <Modal
        title="目录预览"
        open={previewOpen}
        onCancel={() => setPreviewOpen(false)}
        footer={[
          <Button key="close" onClick={() => setPreviewOpen(false)}>
            关闭
          </Button>,
        ]}
        width={720}
        destroyOnHidden
        styles={{
          body: {
            height: PREVIEW_MODAL_BODY_HEIGHT,
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column',
            paddingTop: 16,
          },
        }}
      >
        <div style={{ flexShrink: 0, marginBottom: 12 }}>
          <Typography.Text strong style={{ fontSize: 15 }}>
            {previewTitle}
          </Typography.Text>
          <div style={{ marginTop: 8 }}>
            <Space size={8} wrap>
              <Tag color={previewSource === 'synced' ? 'green' : 'blue'}>
                {previewSource === 'synced' ? '已同步目录' : '规则结构预览'}
              </Tag>
              {previewStats.total > 0 ? (
                <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                  共 {previewStats.total} 个节点 · 最深 {previewStats.maxDepth} 级 · 点击节点逐层展开
                </Typography.Text>
              ) : null}
            </Space>
          </div>
          {previewSource === 'synced' && previewSyncedAt ? (
            <Typography.Text type="secondary" style={{ display: 'block', marginTop: 4, fontSize: 12 }}>
              最近同步：{formatDate(previewSyncedAt)}
            </Typography.Text>
          ) : null}
        </div>

        <div
          style={{
            flex: 1,
            minHeight: 0,
            height: PREVIEW_TREE_PANEL_HEIGHT,
            overflow: 'auto',
            border: '1px solid var(--ant-color-border-secondary)',
            borderRadius: 8,
            padding: '12px 16px',
            background: 'var(--ant-color-fill-quaternary)',
          }}
        >
          {previewLoading ? (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
              <Spin />
            </div>
          ) : previewRawNodes.length ? (
            <Tree
              blockNode
              showIcon
              showLine
              expandedKeys={previewExpandedKeys}
              onExpand={(keys) => setPreviewExpandedKeys(keys.map(String))}
              treeData={previewTreeData}
            />
          ) : (
            <Empty
              image={Empty.PRESENTED_IMAGE_SIMPLE}
              description="该规则下暂无文档类型，请先在规则详情中维护类型树"
            />
          )}
        </div>
      </Modal>
    </div>
  )
}
